import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography
    , CircularProgress
} from '@mui/material';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

const RejectModal = ({ open, onClose, onConfirm, organizationName, isActionLoading }) => {
    const { t } = useTranslation();
    const [rejectReason, setRejectReason] = useState('');
    const [error, setError] = useState('');
    const CHARACTER_LIMIT = 200;

    const handleChange = (event) => {
        const input = event.target.value;
        setRejectReason(input);

        // Clear error if input is valid
        if (input.length <= CHARACTER_LIMIT) {
            setError('');
        } else {
            setError(t("Reason must be {{limit}} characters or less", { limit: CHARACTER_LIMIT }));
        }
    };

    const handleSubmit = () => {
        if (rejectReason.trim() === '') {
            setError(t("Please provide a reason for rejection"));
            return;
        }

        if (rejectReason.length > CHARACTER_LIMIT) {
            setError(t("Reason must be {{limit}} characters or less", { limit: CHARACTER_LIMIT }));
            return;
        }

        onConfirm(rejectReason);
        setRejectReason(''); // Reset the input after submission
    };

    const handleClose = () => {
        setRejectReason(''); // Clear input when closing
        setError(''); // Clear error when closing
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            sx={{ borderRadius: "10px" }}
        >
            <Box sx={{
                border: "1px solid #A5DCF3",
                borderRadius: "10px",
                boxShadow: "-2px 2px 5px 0px #00000040",
                padding: "40px 80px"
            }}>
                <Typography
                    sx={{
                        fontFamily: "Roboto",
                        fontWeight: 500,
                        fontSize: "28px",
                        lineHeight: "100%",
                        textAlign: "center",
                        color: "#EE3B2B"
                    }}
                >
                    {t("Reject Organization Request")}
                </Typography>
                <DialogContent sx={{ mt: 2 }}>
                    <Typography
                        variant="body1"
                        sx={{
                            fontFamily: "Roboto",
                            fontWeight: 500,
                            fontSize: "20px",
                            lineHeight: "100%",
                            color: "#1F242E",
                            mb: 2
                        }}
                    >
                        {organizationName
                          ? t('Reason for rejection "{{name}}"', { name: organizationName })
                          : t("Reason for rejection")}
                    </Typography>
                    <TextField
                        autoFocus
                        placeholder={t("Provide a brief reason for rejection, e.g., Missing required details, Invalid contact information, or Insufficient organization profile.")}
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={rejectReason}
                        onChange={handleChange}
                        error={!!error}
                        helperText={error || t("{{count}}/{{limit}} characters", { count: rejectReason.length, limit: CHARACTER_LIMIT })}
                        // Remove the label prop completely and use placeholder instead
                        label=""
                        InputLabelProps={{
                            shrink: false,
                        }}
                        sx={{
                            mb: 2,
                            width: "100%",
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "16px",
                                "& fieldset": {
                                    borderWidth: "2px",
                                    borderColor: "#183059",
                                },
                                "&:hover fieldset": {
                                    borderColor: "#183059",
                                },
                                "&.Mui-focused fieldset": {
                                    borderColor: "#183059",
                                    borderWidth: "2px",
                                }
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "16px 24px",
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, justifyContent: "center" }}>
                    <Button
                        onClick={handleClose}
                        variant="outlined"
                        sx={{
                            fontSize: "20px",
                            width: "122px",
                            height: "55px",
                            borderRadius: "8px",
                            borderWidth: "1px",
                            padding: "16px 24px",
                            background: "#F2F2F3",
                            border: "1px solid #414958",
                            color: "#1F242E",
                            textTransform: "none"
                        }}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={rejectReason.length > CHARACTER_LIMIT || rejectReason.trim() === ''}
                        sx={{
                            fontSize: "20px",
                            width: "125px",
                            height: "55px",
                            borderRadius: "8px",
                            padding: "16px 24px",
                            background: "#EE3B2B",
                            color: "#FFFFFF",
                            "&:hover": {
                                backgroundColor: "#d63526"
                            },
                            "&:disabled": {
                                opacity: 0.7
                            },
                            textTransform: "none"
                        }}
                    >
                        {isActionLoading ? <><CircularProgress /></> : t("Reject")}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

RejectModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    organizationName: PropTypes.string
};

export default RejectModal;